import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { PermissionKeySchema } from '../permissions/permission-key/permission-key';
import { RoleKeySchema } from '../roles/role/role';

/**
 * A role as it appears on a user's access panel.
 *
 * `permissions` is what this role **contributes** — the attribution
 * `rolePermissions` (a flat union of every assigned role) structurally cannot
 * carry. Without it a client that wants to say "the `reviewer` role is why they
 * hold `verification.review`" has to fetch `GET /admin/roles/:id` once per
 * assigned role, which is the N+1 W6 exists to forbid; the server already reads
 * the grouping and used to discard it.
 */
export const AssignedRoleSchema = z.object({
  id: UuidSchema,
  key: RoleKeySchema,
  name: z.string(),
  isSystem: z.boolean(),
  permissions: z.array(PermissionKeySchema),
});

export type AssignedRole = z.infer<typeof AssignedRoleSchema>;

/**
 * The **effective-permissions preview** (03-access-control): every input and
 * the computed result, so an admin can see *why* someone can do something
 * before saving — role permissions, direct grants, direct denies, and the
 * resolved set `(roles ∪ grants) − denies` with wildcards expanded.
 *
 * It is also what the **reviewed save** answers with, so a client never re-reads
 * to learn what it just did.
 */
export const EffectivePermissionsSchema = z.object({
  userId: UuidSchema,
  isSuperAdmin: z.boolean(),
  roles: z.array(AssignedRoleSchema),
  rolePermissions: z.array(PermissionKeySchema),
  directGrants: z.array(PermissionKeySchema),
  directDenies: z.array(PermissionKeySchema),
  effective: z.array(PermissionKeySchema),
});

export type EffectivePermissions = z.infer<typeof EffectivePermissionsSchema>;
